import moment from 'moment';
import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'ui-kit';
import { BUTTON_TYPES, URLS, MESSAGES, PERMISSIONS, USER_ROLE_TYPES } from '_constants';
import BackPageButton from 'modules/BackPageButton';
import { notification } from 'utils/services';
import { OnlineIndicator, AccessControl, IsMassAccount, HasAccess, IsAzAccount } from 'modules';
import { useOfflineMode } from 'hooks';
import { getDataFromInclude, capitalizeFirstLetter } from 'utils/custom';
import { AttachToCampaignModal, DetachFromCampaignModal, RestoreDeleteModal } from 'modules/Modals';
import { VisitEntity, RetailerEntity, SurveyEntity, CollectEntity } from '_entities';
import apiData from '_entities/old/api/config';
import { PAGE_TYPE } from '../VisitView/constants';
import { VISIT_ROLE_RULE_SET, visitPermissionData } from './visitEditAccess';
import * as Styled from './styles';

const { getVisitData, getVisitInfo } = VisitEntity.selectors;
const { sendViolationPDF, bulkDeleteVisits, bulkRestoreVisits } = VisitEntity.actions;
const { getRetailerInfo } = RetailerEntity.selectors;
const { getSingleSurveyForm } = SurveyEntity.selectors;
const { getAssignmnetsAttachedToCampaign } = CollectEntity.actions;

const EntityHeader = ({
  pageType,
  submitFunction,
  toView,
  title,
  showOnlineIndicator,
  updateVisitInfo,
  viewUrl,
  isInspectorMode,
}) => {
  const data = {
    [PAGE_TYPE.ADD]: useSelector(getSingleSurveyForm),
    [PAGE_TYPE.EDIT]: useSelector(getVisitData),
    [PAGE_TYPE.VIEW]: useSelector(getVisitData),
  };
  const { baseUrl } = apiData;
  const entityData = data[pageType];
  const visitInfo = useSelector(getVisitInfo);
  const retailerInfo = useSelector(getRetailerInfo);
  const [isAssignmentsModalOpen, setIsAssignmentsModalOpen] = useState(false);
  const [isDetachModalOpen, setIsDetachModalOpen] = useState(false);
  const [assignmentsAttached, setAssignmentsAttached] = useState({});
  const [modalStates, setModalStates] = useState({ isOpen: false, modalType: '', modalMessage: '', payload: {} });
  const [isRestoreDeleteBtnDisabled, setIsRestoreDeleteBtnDisabled] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();
  const offlineMode = useOfflineMode();
  const entityAttributes = entityData.data?.attributes;
  const entityIncluded = entityData.included;
  const { headerTitle, editPageUrl } = visitInfo;
  const isMassAccount = IsMassAccount();
  const isAzAccount = IsAzAccount();

  const agency = entityIncluded && getDataFromInclude(entityIncluded, 'Agency');
  const form = entityIncluded && getDataFromInclude(entityIncluded, 'Form');
  const retailer = (entityIncluded && getDataFromInclude(entityIncluded, 'Retailer')) || retailerInfo;
  const linkedCampaigns = useMemo(() => entityAttributes?.linked_campaigns || [], [entityAttributes?.linked_campaigns]);
  const address = retailer?.attributes?.address;
  const address2 = retailer?.attributes?.address2;
  const city = retailer?.attributes?.city;
  const state = retailer?.attributes?.state;
  const zipcode = retailer?.attributes?.zipcode;
  const retailerName = retailer?.attributes && capitalizeFirstLetter(retailer.attributes.name);
  const formName = form?.attributes?.name || entityAttributes?.name;
  const buttonText = pageType === PAGE_TYPE.VIEW ? 'Edit' : 'Save changes';
  const isDeleted = entityAttributes?.deleted;
  const relationships = entityData?.data?.relationships;
  const visitCreatorId = relationships?.user?.data?.id;
  const userCreator = entityIncluded && getDataFromInclude(entityIncluded, 'User', visitCreatorId);
  const { MULTI_ACCOUNT_MANAGER, ACCOUNT_MANAGER } = USER_ROLE_TYPES;

  // Shared with the Notes section's edit pencils (visitEditAccess), so both follow one rule.
  const permissionData = visitPermissionData(entityData);

  const restoreRuleSet = { all: [ACCOUNT_MANAGER, MULTI_ACCOUNT_MANAGER] };

  const roleRuleSet = VISIT_ROLE_RULE_SET;

  const canViewCampaign = HasAccess(PERMISSIONS.VIEW_CAMPAIGN, permissionData, roleRuleSet);

  const getAttachedCampaign = async () => {
    if (pageType !== PAGE_TYPE.VIEW || !canViewCampaign) return;

    try {
      const { id: retailerId } = data[PAGE_TYPE.VIEW].data?.relationships.retailer.data;
      const { id: formId } = data[PAGE_TYPE.VIEW].data?.relationships.form.data;
      const userCreatorEmail = userCreator?.attributes?.email;
      const encodedUserCreatorEmail = encodeURIComponent(userCreatorEmail);

      const queryParams = `form=${formId}&retailer=${retailerId}&email=${encodedUserCreatorEmail}&visit=${id}`;

      const res = await dispatch(getAssignmnetsAttachedToCampaign(queryParams));
      setAssignmentsAttached(res);
    } catch (e) {
      // Non-fatal: attached-campaign list stays empty if the fetch fails.
    }
  };

  // Re-run only when pageType changes; getAttachedCampaign is intentionally excluded
  // (it is re-created each render and would otherwise re-fetch continuously).
  useEffect(() => {
    if (pageType === PAGE_TYPE.VIEW) {
      getAttachedCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType]);

  const violationSummaryHandler = useCallback(async () => {
    const date = entityData.data.attributes.submitted;
    const violationDate = {
      date: moment(date).format('ll'),
      time: `${moment(date).format('LTS')}`,
    };
    try {
      await dispatch(sendViolationPDF(id, violationDate));
      notification.success(MESSAGES.VISIT.VIOLATION_PDF.SUCCESS);
    } catch (err) {
      notification.error(MESSAGES.VISIT.VIOLATION_PDF.ERROR);
    }
  }, [dispatch, entityData, id]);

  const getCampaigns = useCallback(() => {
    if (!linkedCampaigns.length) return null;
    return linkedCampaigns.map((c, index) => (
      <React.Fragment key={c.id}>
        <Link to={`${URLS.campaign}/${c.id}`}>{c.campaign_name}</Link>
        {index < linkedCampaigns.length - 1 && ', '}
      </React.Fragment>
    ));
  }, [linkedCampaigns]);

  const buttonClickHandler = useCallback(() => {
    switch (pageType) {
      case PAGE_TYPE.VIEW:
        history.push(`${editPageUrl}/${entityData.data.id}`);
        break;
      case PAGE_TYPE.EDIT:
        submitFunction();
        break;
      default:
        break;
    }
  }, [pageType, history, editPageUrl, entityData, submitFunction]);

  const handleOpenAssignmentsModal = () => {
    setIsAssignmentsModalOpen(() => true);
  };

  const handleCloseAssignmentsModal = () => {
    setIsAssignmentsModalOpen(() => false);
  };

  const handleOpenDetachModal = () => setIsDetachModalOpen(true);

  const handleCloseDetachModal = () => setIsDetachModalOpen(false);

  const handleDeleteVisits = async (payload) => {
    try {
      await dispatch(bulkDeleteVisits(payload));
      await updateVisitInfo();
      notification.success('Visits was successfully deleted');
      history.push(viewUrl);
    } catch (err) {
      const errorMessageArray = err?.response?.data?.errors;

      if (Array.isArray(errorMessageArray)) {
        const [errorMessage] = errorMessageArray;
        notification.error(errorMessage.detail);
      } else {
        notification.error('Something went wrong during the request');
      }
    }
  };

  const handleRestoreDeleteButton = async () => {
    const payload = { data: [{ type: 'Visit', id }] };
    setIsRestoreDeleteBtnDisabled(true);

    try {
      if (isDeleted) {
        await dispatch(bulkRestoreVisits(payload));
        await updateVisitInfo();
        notification.success('Visits were successfully restored');
      } else {
        setModalStates(() => ({
          isOpen: true,
          modalType: 'delete',
          modalMessage: 'Are you sure you want to delete this visit?',
          payload,
        }));
      }
    } catch (err) {
      const errorMessageArray = err?.response?.data?.errors;

      if (Array.isArray(errorMessageArray)) {
        const [errorMessage] = errorMessageArray;
        notification.error(errorMessage.detail || errorMessage);
      } else {
        notification.error('Something went wrong during the request');
      }
      setIsRestoreDeleteBtnDisabled(false);
    }
  };

  const closeRestoreDeleteModal = () => {
    setModalStates({ isOpen: false, modalType: '' });
  };

  const address2String = address2 ? `${address2}, ` : '';
  const retailerAddress = `${address}, ${address2String}${city}, ${state}, ${zipcode}`;
  const newHeaderTitle = headerTitle || title;

  return (
    <Styled.Wrapper>
      <Styled.TopWrapper>
        <Styled.TitleCol>
          <BackPageButton />
          <Styled.Title>
            {isAzAccount ? `${newHeaderTitle.replace('enforcement', 'inspection')} ${id}` : newHeaderTitle}
          </Styled.Title>
          {showOnlineIndicator && (
            <Styled.OnlineWrapper>
              <OnlineIndicator />
            </Styled.OnlineWrapper>
          )}
          {pageType !== PAGE_TYPE.VIEW && <Styled.SubTitle>* - required fields</Styled.SubTitle>}
        </Styled.TitleCol>
        {pageType !== PAGE_TYPE.ADD && (
          <Styled.ButtonWrap>
            {pageType === PAGE_TYPE.VIEW && isDeleted && (
              <AccessControl roleRuleSet={restoreRuleSet}>
                <Button
                  onClick={handleRestoreDeleteButton}
                  disabled={isRestoreDeleteBtnDisabled}
                  text="Restore visit"
                  variant={BUTTON_TYPES.DANGER}
                />
              </AccessControl>
            )}
            {pageType === PAGE_TYPE.VIEW && !isDeleted && (
              <AccessControl permission={PERMISSIONS.DELETE_VISIT} roleRuleSet={roleRuleSet} data={permissionData}>
                <Button onClick={handleRestoreDeleteButton} text="Delete visit" variant={BUTTON_TYPES.DARK} />
              </AccessControl>
            )}
            {pageType === PAGE_TYPE.VIEW && !isDeleted && (
              <AccessControl
                permission={[PERMISSIONS.CHANGE_VISIT, PERMISSIONS.VIEW_CAMPAIGN]}
                roleRuleSet={roleRuleSet}
                data={permissionData}
              >
                <Button onClick={handleOpenAssignmentsModal} text="Attach to campaign" variant={BUTTON_TYPES.DANGER} />
              </AccessControl>
            )}
            {pageType === PAGE_TYPE.VIEW && !isDeleted && !!linkedCampaigns.length && (
              <AccessControl
                permission={[PERMISSIONS.CHANGE_VISIT, PERMISSIONS.VIEW_CAMPAIGN]}
                roleRuleSet={roleRuleSet}
                data={permissionData}
              >
                <Button onClick={handleOpenDetachModal} text="Detach from campaign" variant={BUTTON_TYPES.DARK} />
              </AccessControl>
            )}
            {pageType === PAGE_TYPE.VIEW && isMassAccount && form?.attributes?.send_summary_email && !isDeleted && (
              <AccessControl permission={PERMISSIONS.VIEW_OTHER_VIOLATION_INFO} data={relationships}>
                <Button onClick={violationSummaryHandler} text="Email visit summary" variant={BUTTON_TYPES.DARK} />
              </AccessControl>
            )}
            {!isDeleted && pageType === PAGE_TYPE.VIEW && (
              <AccessControl permission={PERMISSIONS.CHANGE_VISIT} roleRuleSet={roleRuleSet} data={permissionData}>
                <Button onClick={buttonClickHandler} text={buttonText} variant={BUTTON_TYPES.DANGER} />
              </AccessControl>
            )}
          </Styled.ButtonWrap>
        )}
      </Styled.TopWrapper>
      <Styled.Info>
        <Styled.P>
          Retailer name:{' '}
          {offlineMode ? (
            <Styled.Details>{retailerName}</Styled.Details>
          ) : (
            <Link to={`${URLS.retailers}/${retailer.id}`}>{retailerName}</Link>
          )}
        </Styled.P>
        <Styled.P>
          Address: <Styled.Details>{retailerAddress}</Styled.Details>
        </Styled.P>
        {formName && (
          <Styled.P>
            Form:
            {isInspectorMode ? (
              <a target="_blank" rel="noreferrer" href={`${baseUrl}admin-ctpost2/surveys/form/${form?.id}`}>
                {formName}
              </a>
            ) : (
              <Styled.Details>{formName}</Styled.Details>
            )}
          </Styled.P>
        )}
        <Styled.P>
          Agency: <Styled.Details>{agency?.attributes.name}</Styled.Details>
        </Styled.P>
        {toView.campaign && getCampaigns() && (
          <AccessControl permission={PERMISSIONS.VIEW_CAMPAIGN} roleRuleSet={roleRuleSet} data={permissionData}>
            <Styled.P>
              Campaign{linkedCampaigns.length > 1 ? 's' : ''}: <Styled.Details>{getCampaigns()}</Styled.Details>
            </Styled.P>
          </AccessControl>
        )}
      </Styled.Info>
      <AttachToCampaignModal
        isOpen={isAssignmentsModalOpen}
        onClose={handleCloseAssignmentsModal}
        assignmentsAttached={assignmentsAttached}
        visitId={id}
        updateVisitInfo={updateVisitInfo}
        linkedCampaignIds={linkedCampaigns.map((c) => c.id)}
      />
      <DetachFromCampaignModal
        isOpen={isDetachModalOpen}
        onClose={handleCloseDetachModal}
        visitId={id}
        linkedCampaigns={linkedCampaigns}
        updateVisitInfo={updateVisitInfo}
      />
      {modalStates.isOpen && (
        <RestoreDeleteModal
          type={modalStates.modalType}
          isOpen={modalStates.isOpen}
          payload={modalStates.payload}
          onClose={closeRestoreDeleteModal}
          postRestoreOrDelete={handleDeleteVisits}
          modalMessage={modalStates.modalMessage}
          modalType={modalStates.modalType}
          page="Retailers"
          dispatch={dispatch}
        />
      )}
    </Styled.Wrapper>
  );
};

EntityHeader.propTypes = {
  pageType: PropTypes.string.isRequired,
  title: PropTypes.string,
  toView: PropTypes.object,
  submitFunction: PropTypes.func,
  showOnlineIndicator: PropTypes.bool,
  updateVisitInfo: PropTypes.func,
  viewUrl: PropTypes.string,
  isInspectorMode: PropTypes.bool,
};

EntityHeader.defaultProps = {
  toView: {
    campaign: true,
  },
  submitFunction: () => {},
  title: '',
  showOnlineIndicator: false,
  updateVisitInfo: () => {},
  viewUrl: '',
  isInspectorMode: false,
};

export default memo(EntityHeader);
